/*
 * Copyright (c) 2020, WSO2 Inc. (http://www.wso2.com). All Rights Reserved.
 *
 * This software is the property of WSO2 Inc. and its suppliers, if any.
 * Dissemination of any information or reproduction of any material contained
 * herein is strictly forbidden, unless permitted by WSO2 in accordance with
 * the WSO2 Commercial License available at http://wso2.com/licenses.
 * For specific language governing the permissions and limitations under
 * this license, please see the license as well as any agreement you’ve
 * entered into with WSO2 governing the purchase of this software and any
 * associated services.
 */

import React from 'react';
import { connect } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { FormattedMessage, useIntl } from 'react-intl';

import { makeStyles } from '@material-ui/core/styles';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import Button from '@material-ui/core/Button';
import Rating from '@material-ui/lab/Rating';
import Popover from '@material-ui/core/Popover';

import ApiHoverRating from './ApiHoverRating';
import CustomIcons from '../../../icons/CustomIcons';
import OutlineButton from '../../../elements/OutlineButton';

import * as Utils from '../../../data/utils';
import { ApiReducer, ApplicationState, RatingsReducer, UserReducer } from '../../../data/interfaces';
import {PATH_SEPARATOR} from "../../../data/constants";

const useStyles = makeStyles((theme) => ({
    root: {
        flexGrow: 1,
    },
    gridText: {
        ...theme.custom.apiOverviewPage.tabs.overview.apiRatingBox
    },
    textVCenter1: {
        ...theme.custom.apiOverviewPage.tabs.overview.apiRatingTextCover
    },
    textVCenter2: {
        ...theme.custom.apiOverviewPage.tabs.overview.apiRatingStars
    },
    rectangle: {
        ...theme.custom.apiOverviewPage.tabs.overview.apiRatingRectangle
    },
    ratingButton: {
        ...theme.custom.apiOverviewPage.tabs.overview.apiRatingButton
    },
    ratingPopover: {
        paddingLeft: theme.spacing(2),
        paddingRight: theme.spacing(2),
    },
    ratingText: {
        ...theme.custom.apiOverviewPage.tabs.overview.apiRatingText
    }
}));

interface ApiRatingProps {
    apiReducer: ApiReducer,
    ratingsReducer: RatingsReducer,
    userReducer: UserReducer,
    hideTryOut?: boolean;
    disableTryOut?: boolean;
}

function ApiRating({ apiReducer, ratingsReducer, userReducer, hideTryOut, disableTryOut }: ApiRatingProps) {
    const intl = useIntl();
    const classes = useStyles();
    const { api } = apiReducer;
    const { ratings } = ratingsReducer;
    const { apimTokenObtained, selectedOrg } = userReducer;
    const selectedOrgHandleName= selectedOrg.handle;
    const [anchorEl, setAnchorEl] = React.useState<HTMLButtonElement | null>(null);
    const open = Boolean(anchorEl);
    const history = useHistory();

    let apiId = '';
    let ratingValue = "0.0";
    let ratingCount = 0;

    if (Utils.isValidData(api)) {
        apiId = api.id;
        ratingValue = api.avgRating;
    }

    if (Utils.isValidData(ratings)) {
        ratingCount = ratings.count;
        ratingValue = ratings.avgRating;
    }

    const handleTryApiClick = () => {
        history.push(PATH_SEPARATOR + selectedOrgHandleName + '/apis/' + apiId + '/tryout');
    }

    const handleRatingClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleRatingClose = () => {
        setAnchorEl(null);
    };

    return (
        <div className={classes.root}>
            <Grid container spacing={2} data-testid="rating-container">
                <Grid item xs={12} lg={12}>
                    <Grid item container spacing={2} className={classes.gridText}>
                        <Grid item xs={4} sm={2} md={2} lg={2} className={classes.textVCenter1}>
                            <Typography className={classes.ratingText}>
                                <FormattedMessage
                                    id="api.apiOverview.apiRating.labels.rating"
                                    defaultMessage="Rating"
                                />
                            </Typography>
                        </Grid>
                        <Grid item xs={6} sm={4} md={4} lg={4} className={classes.textVCenter2}>
                            <Rating
                                name="simple-controlled"
                                value={parseFloat(ratingValue)}
                                precision={0.5}
                                size="small"
                                readOnly
                            />
                        </Grid>
                        <Grid item xs={2} sm={1} md={1} lg={1} className={classes.textVCenter2}>
                            <div className={classes.rectangle}></div>
                        </Grid>
                        <Grid item xs={6} sm={3} md={3} lg={3} className={classes.textVCenter1}>
                            <Typography className={classes.ratingText}>{ratingValue} ({ratingCount})</Typography>
                        </Grid>
                        <Grid item xs={6} sm={1} md={1} lg={1}>
                            <Button
                                variant="outlined"
                                color="default"
                                className={classes.ratingButton}
                                onClick={handleRatingClick}
                            >
                                <CustomIcons icon="rating-button-icon" />
                            </Button>
                            <Popover
                                open={open}
                                anchorEl={anchorEl}
                                onClose={handleRatingClose}
                                anchorOrigin={{
                                    vertical: 'bottom',
                                    horizontal: 'center',
                                }}
                                transformOrigin={{
                                    vertical: 'top',
                                    horizontal: 'center',
                                }}
                            >
                                {
                                    apimTokenObtained ?
                                        <ApiHoverRating />
                                        :
                                        <Typography variant="caption" className={classes.ratingPopover}>
                                            <i>
                                                <FormattedMessage
                                                    id="api.apiOverview.apiRating.messages.signIn"
                                                    defaultMessage="Please sign in to submit user rating"
                                                />
                                            </i>
                                        </Typography>
                                }
                            </Popover>
                        </Grid>
                    </Grid>
                </Grid>
                {(!hideTryOut && apimTokenObtained) &&
                    <Grid item lg={12} md={12} xs={12} style={{ textAlign: 'left' }}>
                        <OutlineButton
                            data-testid="button-try-out"
                            onClick={handleTryApiClick}
                            buttonLabel={intl.formatMessage({
                                id: "api.apiOverview.apiRating.buttons.label.tryApi",
                                defaultMessage: "Try API"
                            })}
                            startIcon={<CustomIcons icon="try-button-icon" />}
                            disabled={disableTryOut}
                        />
                    </Grid>
                }
            </Grid>
        </div>
    );
}

const mapStateToProps = (state: ApplicationState) => {
    return {
        apiReducer: state.apiReducer,
        ratingsReducer: state.ratingsReducer,
        userReducer: state.userReducer,
    }
}

export default connect(mapStateToProps)(ApiRating);
